import {
  Button,
  Flex,
  Icon,
  Image,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
  Tooltip,
  useBreakpointValue,
  useCallbackRef,
} from '@chakra-ui/react';
import { isBefore } from 'date-fns';
import { useResources } from 'datx-jsonapi-react';
import { useRouter } from 'next/router';
import { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CalendarIcon from '@/assets/icons/ic-calendar-plus.svg';
import Close from '@/assets/icons/ic-close.svg';

import { SCHEDULE_TAB } from '@/components/ui/features/schedule/static';
import { useAddEventToCalendarLinks } from '@/components/ui/shared/buttons/AddToCalendar/AddToCalendar.hooks';
import {
  iconStyleProps,
  menuButtonStyleProps,
  menuItemStyleProps,
  simpleMButtonStyleProps,
} from '@/components/ui/shared/buttons/AddToCalendar/AddToCalendar.styles';
import { IAddToCalendarSessions } from '@/components/ui/shared/buttons/AddToCalendar/type';
import { IToastType } from '@/enums/ToastType';
import { useAddToCalendarDescription } from '@/hooks/useAddToCalendarDescription';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import useSession from '@/hooks/useSession';
import { useStores } from '@/hooks/useStores';
import { useToast } from '@/hooks/useToast';
import { queryAgendaSessionsForEvent } from '@/queries/agenda-session';

export const AddToCalendarSession: FC<IAddToCalendarSessions> = ({
  session,
  color,
  size = 34,
  iconSize = 20,
  simpleMenuButton = false,
  timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  mutateSession,
  ...rest
}) => {
  const { eventId, active_tab } = useQueryParams<{ eventId: string; active_tab: SCHEDULE_TAB }>({
    eventId: StringParam,
    active_tab: StringParam,
  });
  const { thinProfile } = useSession();
  const { store } = useStores();
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [openModal, setOpenModal] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const { asPath } = useRouter();
  const handleMousedown = useCallbackRef((e) => {
    e.stopPropagation();
  });

  const { data: agendaSessions = [], mutate: mutateAgenda } = useResources(
    queryAgendaSessionsForEvent(thinProfile?.id)
  );

  const description = useAddToCalendarDescription(session.id);

  const handleMenuListClick = useCallbackRef((e) => {
    e.stopPropagation();
  });

  const handleMenuButtonClick = useCallbackRef((e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenModal(true);
  });

  const handleMenuOpen = () => {
    setOpenModal(true);
    setTooltipOpen(false);
    document.addEventListener('mousedown', handleMousedown, { capture: true });
  };
  const handleMenuClose = () => {
    setOpenModal(false);
    setTooltipOpen(false);
    document.removeEventListener('mousedown', handleMousedown);
  };

  const handleMouseEnter = () => {
    setTooltipOpen(true);
  };

  const handleMouseLeave = () => {
    setTooltipOpen(false);
  };

  const url = process.env.NEXT_PUBLIC_BASE_URL + asPath.substring(1);
  const calendarLinks = useAddEventToCalendarLinks({
    allDayEvent: false,
    timeZone,
    url,
    description,
    location: session.venue || '',
    startUtc: session.startsAt,
    endUtc: session.endsAt,
    title: session.name,
    ...rest,
  });
  const { t } = useTranslation();

  const menuProps = simpleMenuButton ? simpleMButtonStyleProps : menuButtonStyleProps;

  const isFinished = isBefore(new Date(session.endsAt), new Date());

  if (isFinished) return null;

  const myAgendaSession = agendaSessions.find((as) => as.eventSessionId === parseInt(session.id));

  async function handleAddAgenda() {
    if (!thinProfile) return;
    try {
      await store.request(`thin_profiles/${thinProfile?.id}/agenda_sessions`, 'POST', {
        data: {
          type: 'agenda_sessions',
          id: '',
          attributes: {
            event_id: eventId,
            event_session_id: session.id,
          },
        },
      });
      mutateAgenda();
      toast({ status: IToastType.INFO, title: t('schedule.myAgenda.toast.successfullyAdded') });
    } catch (error) {
      toast({ status: IToastType.ERROR, title: t('schedule.myAgenda.toast.error') });
    }
  }

  async function handleRemoveAgenda() {
    if (!thinProfile || !myAgendaSession) return;
    try {
      await store.request(`thin_profiles/${thinProfile?.id}/agenda_sessions/${myAgendaSession.id}`, 'DELETE');
      mutateAgenda();
      if (mutateSession) {
        mutateSession();
      }
      toast({ status: IToastType.INFO, title: t('schedule.myAgenda.toast.successfullyRemoved') });
    } catch (error) {
      toast({ status: IToastType.ERROR, title: t('schedule.myAgenda.toast.error') });
    }
  }

  const isMyAgendaTab = active_tab === SCHEDULE_TAB.MY_AGENDA;

  function handleOnMouseDown(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    e.stopPropagation();
    e.preventDefault();
  }

  return (
    <Menu onOpen={handleMenuOpen} onClose={handleMenuClose} isOpen={openModal}>
      <Tooltip
        label={myAgendaSession && isMyAgendaTab ? t('addToCalendar.agendaRemove') : t('addToCalendar.tooltip')}
        placement="start"
        isDisabled={openModal}
        hasArrow
        bg={'white'}
        isOpen={tooltipOpen && !openModal}
      >
        <MenuButton
          aria-label="Add to calendar"
          w={`${size}px`}
          h={`${size}px`}
          onClick={handleMenuButtonClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          {...menuProps}
        >
          <Icon
            as={CalendarIcon}
            w={`${iconSize}px`}
            h={`${iconSize}px`}
            color={color}
            _groupHover={{ color: 'gray.50' }}
            {...iconStyleProps}
          />
        </MenuButton>
      </Tooltip>
      <Portal>
        <MenuList
          maxW="none"
          w={isMobile && '100vw'}
          h={isMobile && '100vh'}
          position={isMobile ? 'fixed' : 'static'}
          rootProps={isMobile && { transform: 'none !important', height: '100vh', width: '100vw' }}
          onMouseDown={handleOnMouseDown}
          onClick={handleMenuListClick}
          color={'gray.500'}
          zIndex={200}
        >
          {isMobile && (
            <Flex justifyContent="flex-end" pr={2}>
              <Button size="xs" onClick={() => setOpenModal(false)} variant="unstyled">
                <Close css={{ width: '1.5rem', color: 'black' }} />
              </Button>
            </Flex>
          )}
          {!myAgendaSession && thinProfile && (
            <MenuItem onClick={handleAddAgenda} {...menuItemStyleProps}>
              <Image boxSize={5} mr={2.5} src={'/images/underlineLogo.svg'} />
              <Text fontSize="md" fontWeight={100}>
                {t('addToCalendar.agendaAdd')}
              </Text>
            </MenuItem>
          )}
          {myAgendaSession && thinProfile && (
            <MenuItem onClick={handleRemoveAgenda} {...menuItemStyleProps}>
              <Image boxSize={5} mr={2.5} src={'/images/underlineLogo.svg'} />
              <Text fontSize="md" fontWeight={100}>
                {t('addToCalendar.agendaRemove')}
              </Text>
            </MenuItem>
          )}
          {!isMyAgendaTab && (
            <Fragment>
              <MenuItem
                as={Link}
                isExternal
                href={calendarLinks.ics}
                download={'Apple Calendar'}
                {...menuItemStyleProps}
              >
                <Image boxSize={5} mr={2.5} src={'/images/apple-calendar.svg'} />
                <Text fontSize="md" fontWeight={100}>
                  {t('addToCalendar.apple')}
                </Text>
              </MenuItem>
              <MenuItem as={Link} isExternal href={calendarLinks.google} {...menuItemStyleProps}>
                <Image boxSize={5} mr={2.5} src={'/images/google.svg'} />
                <Text fontSize="md" fontWeight={100}>
                  {`${t('addToCalendar.goggle')} (${t('common.online')})`}
                </Text>
              </MenuItem>
              <MenuItem as={Link} isExternal href={calendarLinks.office365} {...menuItemStyleProps}>
                <Image boxSize={5} mr={2.5} src={'/images/office365.svg'} />
                <Text fontSize="md" fontWeight={100}>
                  {`${t('addToCalendar.office365')} (${t('common.online')})`}
                </Text>
              </MenuItem>
              <MenuItem
                as={Link}
                isExternal
                href={calendarLinks.ics}
                download={'Outlook Calendar'}
                {...menuItemStyleProps}
              >
                <Image boxSize={5} mr={2.5} src={'/images/outlook.svg'} />
                <Text fontSize="md" fontWeight={100}>
                  {t('addToCalendar.outlook')}
                </Text>
              </MenuItem>
              <MenuItem as={Link} isExternal href={calendarLinks.outlook} {...menuItemStyleProps}>
                <Image boxSize={5} mr={2.5} src={'/images/outlookcom.svg'} />
                <Text fontSize="md" fontWeight={100}>
                  {`${t('addToCalendar.outlookcom')} (${t('common.online')})`}
                </Text>
              </MenuItem>
              <MenuItem as={Link} isExternal href={calendarLinks.yahoo} {...menuItemStyleProps}>
                <Image boxSize={5} mr={2.5} src={'/images/yahoo.svg'} />
                <Text fontSize="md" fontWeight={100}>
                  {`${t('addToCalendar.yahoo')} (${t('common.online')})`}
                </Text>
              </MenuItem>
            </Fragment>
          )}
        </MenuList>
      </Portal>
    </Menu>
  );
};
